
import React, { useState, useRef, useCallback, useEffect } from "react";
import * as Material from "@mui/material";
import { Text, Input, Table, StatusChip } from "@fragments";
import {
    FilterListRounded,
    Circle,
    Group,
    CalendarToday,
    AttachMoneyRounded,
    PixRounded,
    ArrowDropDownRounded
} from "@mui/icons-material";

import { ReactComponent as MastercardIcon } from '@assets/custom-icons/mastercard.svg';
import { ReactComponent as EloIcon } from '@assets/custom-icons/elo.svg';
import { ReactComponent as VisaIcon } from '@assets/custom-icons/visa.svg';

import api from "@helpers/api.js";
import Parser from "@helpers/parser.js";
import { useCommunity } from "contexts/community";

import Popper from "@components/Popper";
import { UserAvatar } from "@components/Community";
import Datepicker from "react-advance-datepicker";

import SearchBar from "../../components/SearchBar";
import MetricCard from "../../components/MetricCard";
import Filter from "../../components/Filter";

import "./styles.scss";

export default function ChargesSection() {
    const sharedPopperRef = useRef();
    const { communities } = useCommunity();

    // Estados compartilhados entre os componentes
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedCommunity, setSelectedCommunity] = useState('all');
    const [searchMode, setSearchMode] = useState('email');

    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null
    });

    const searchQueryRef = useRef('');

    // Estados de dados e loading centralizados
    const [charges, setCharges] = useState([]);
    const [summary, setSummary] = useState({
        paid: { records: 0, amount: 0 },
        refunded: { records: 0, amount: 0 },
        pending: { records: 0, amount: 0 }
    });
    const [loading, setLoading] = useState(true);

    // Estados de paginação
    const [pagination, setPagination] = useState({
        pages: 0,
        records: 0,
        page: 1
    });

    const rowsSize = 15;

    const fetchData = useCallback(async (searchValue = searchQueryRef.current, pageNumber = pagination.page) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            if (selectedStatus !== 'all') {
                params.append('filter[status]', selectedStatus);
            }

            if (selectedCommunity !== 'all') {
                params.append('community', selectedCommunity);
            }

            // Adicionar filtros de data
            if (dateRange.startDate && dateRange.endDate) {
                const dateFrom = dateRange.startDate.split('/').reverse().join('-');
                const dateTo = dateRange.endDate.split('/').reverse().join('-');

                if (dateFrom) {
                    params.append('filter[date_from]', dateFrom);
                }
                if (dateTo) {
                    params.append('filter[date_to]', dateTo);
                }
            }

            if (searchValue.trim()) {
                if (searchMode === 'email') {
                    params.append('filter[email]', searchValue.trim());
                } else {
                    params.append('filter[name]', searchValue.trim());
                }
            }

            // Adicionar parâmetros de paginação
            params.append('page', pageNumber.toString());
            params.append('per_page', rowsSize);

            const { data } = await api.get(`charge/search?${params.toString()}`);

            setCharges(data.data || []);
            setSummary(data.summary || {
                paid: { records: 0, amount: 0 },
                refunded: { records: 0, amount: 0 },
                pending: { records: 0, amount: 0 }
            });

            // Atualizar dados de paginação
            setPagination({
                pages: data.pages || 0,
                records: data.records || 0,
                page: data.page || 1
            });

        } catch (error) {
            console.error('Error fetching charges:', error);
            setCharges([]);
            setSummary({
                paid: { records: 0, amount: 0 },
                refunded: { records: 0, amount: 0 },
                pending: { records: 0, amount: 0 }
            });
            setPagination({
                pages: 0,
                records: 0,
                page: 1
            });
        } finally {
            setLoading(false);
        }
    }, [selectedStatus, selectedCommunity, dateRange, searchMode, pagination.page]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchData(searchQueryRef.current, 1);
    }, [selectedStatus, selectedCommunity, dateRange, searchMode]);

    const executeSearch = useCallback(() => {
        fetchData(searchQueryRef.current);
    }, [fetchData]);

    const handlePageChange = useCallback((newPage) => {
        fetchData(searchQueryRef.current, newPage);
    }, [fetchData]);

    function FiltersRow() {
        const statusOptions = [
            { id: 'all', label: 'Todos' },
            { id: 'paid', label: 'Paga' },
            { id: 'refunded', label: 'Reembolsada' },
            { id: 'pending', label: 'Pendente' },
        ];

        const communityOptions = [
            { id: 'all', label: 'Todas' },
            ...(communities || []).filter(comm => comm?.isAdmin()).map(comm => ({
                id: comm.id,
                label: comm.name
            }))
        ];


        function handleStatusFilterClick(event) {
            const options = statusOptions.map(option => ({
                ...option,
                onClick: () => handleStatusSelect(option.id)
            }));

            sharedPopperRef.current.open(options, 'bottom', event.currentTarget);
        }

        function handleCommunityFilterClick(event) {
            const options = communityOptions.map(option => ({
                ...option,
                onClick: () => handleCommunitySelect(option.id)
            }));

            sharedPopperRef.current.open(options, 'bottom', event.currentTarget);
        }

        function handleStatusSelect(status) {
            setSelectedStatus(status);
            sharedPopperRef.current.close();
        }

        function handleCommunitySelect(communityId) {
            setSelectedCommunity(communityId);
            sharedPopperRef.current.close();
        }


        return (
            <>
                <Text.h4 value="Filtros" className="mt-3 mb-1" left={<FilterListRounded />} />

                <div className="filters-row">

                    <Filter
                        name="Status"
                        valueLabel={statusOptions.find(s => s.id === selectedStatus)?.label || 'Todos'}
                        onClick={handleStatusFilterClick}
                        icon={<Circle style={{ fontSize: '16px' }} />}
                    />
                    <Filter
                        name="Comunidade"
                        valueLabel={communityOptions.find(c => c.id === selectedCommunity)?.label || 'Todas as comunidades'}
                        onClick={handleCommunityFilterClick}
                        icon={<Group style={{ fontSize: '16px' }} />}
                    />
                    <Filter
                        className="date-select-range-filter"
                        name="Data"
                        icon={<CalendarToday style={{ fontSize: '16px' }} />}
                        customComponent={
                            <Datepicker
                                inputClassName="date-select-range-input"
                                containerClassName="date-select-range-container"
                                i18n={"pt-br"}
                                maxDate={new Date()}
                                showShortcuts={true}
                                showFooter={true}
                                displayFormat={"DD/MM/YYYY"}
                                value={dateRange}
                                onChange={setDateRange}
                                useRange={true}
                                configs={{
                                    shortcuts: {
                                        today: "Hoje",
                                        yesterday: "Ontem",
                                        currentMonth: "Este mês",
                                        pastMonth: "Mês passado"
                                    },
                                    footer: {
                                        cancel: "Cancelar",
                                        apply: "Filtrar"
                                    }
                                }}
                            />
                        }
                    />

                </div>

                <Popper controlRef={sharedPopperRef}>
                    <div style={{ display: 'none' }} />
                </Popper>
            </>
        )
    }



    function ChargesTableSection() {

        const getMethodIcon = (method, cardBrand) => {
            if (method === 'credit') {
                const brand = cardBrand?.toLowerCase() || 'visa';
                return (
                    <>
                        {{
                            'visa': <VisaIcon />,
                            'elo': <EloIcon />,
                            'mastercard': <MastercardIcon />
                        }[brand]}
                    </>
                );
            } else if (method === 'pix') {
                return <PixRounded />;
            }
            return <AttachMoneyRounded />;
        };

        const formatAmount = (amount, installments) => {
            if (!amount) return 'R$0,00';

            const totalAmount = amount;
            const formattedAmount = Parser.price.brl(totalAmount);

            if (installments && installments > 1) {
                const installmentAmount = totalAmount / installments;
                const formattedInstallment = Parser.price.brl(installmentAmount);
                return `R$${formattedAmount} (${installments}x R$${formattedInstallment})`;
            }

            return `R$${formattedAmount}`;
        };

        const getCardNumber = (charge) => {
            if (charge.method === 'credit' && charge.card?.last_four) {
                return `• ${charge.card.last_four}`;
            }
            return '';
        };

        const handleUserClick = (e, userId) => {
            e.stopPropagation();
            if (e.ctrlKey || e.metaKey) {
                // Abrir em nova aba
                window.open(`/user/${userId}`, '_blank');
            } else {
                // Navegar normalmente
                window.navigate(`/user/${userId}`);
            }
        };

        const columns = [
            {
                id: 'client',
                title: 'Cliente',
                grow: 1,
                className: 'pl-1',
                customRender: (data) => (
                    <div className="client-info">
                        <UserAvatar user={data.user} size={28} />
                        <Text.span contrast={7} weight={400}>
                            {data.user?.username || 'N/A'}
                        </Text.span>
                    </div>
                )
            },
            {
                id: 'status',
                title: 'Status',
                align: 'center',
                minWidth: 120,
                customRender: (data) => {
                    const statusKey = data.status || 'default';
                    const StatusComponent = {
                        'paid': StatusChip.Success,
                        'pending': StatusChip.Alert,
                        'processing': StatusChip.Alert,
                        'canceled': StatusChip.Error,
                        'refunded': StatusChip.Error,
                        'default': StatusChip.Default
                    }[statusKey];

                    return <StatusComponent text={Parser.payment.chargeStatus(data.status)} />;
                }
            },
            {
                id: 'amount',
                title: 'Valor',
                minWidth: 150,
                render: (data) => formatAmount(data.amount, data.installments)
            },
            {
                id: 'method',
                title: 'Método',
                align: 'center',
                minWidth: 110,
                className: 'payment-method-cell',
                customRender: (data) => (
                    <>
                        {getMethodIcon(data.method, data.card?.brand)}
                        {data.method === 'credit' && (
                            <label className="card-number">
                                {getCardNumber(data)}
                            </label>
                        )}
                    </>
                )
            },
            {
                id: 'date',
                title: 'Data',
                minWidth: 120,
                render: (data) => Parser.date.readable(data.created)
            }
        ];

        return (
            <div className="charges-table mt-3 mb-6">
                <Table
                    id="banking-charges-table"
                    data={charges}
                    loading={loading}
                    loadingRows={4}
                    emptyMessage="Nenhuma cobrança encontrada"
                    columns={columns}
                    enablePagination={true}
                    pages={pagination.pages}
                    records={pagination.records}
                    page={pagination.page}
                    onSelectPage={handlePageChange}
                    onRowClick={(item, e) => handleUserClick(e, item.user?.id)}
                    contained
                />
            </div>
        )
    }

    return (
        <div>
            <SearchBar
                searchMode={searchMode}
                setSearchMode={setSearchMode}
                searchQueryRef={searchQueryRef}
                onSearch={executeSearch}
            />

            <FiltersRow />

            <div className="metrics-row mt-3">
                <MetricCard
                    title="Pagas"
                    metric={summary.paid.records}
                    submetric={`R$ ${Parser.price.brl(summary.paid.amount)}`}
                    loading={loading}
                />
                <MetricCard
                    title="Reembolsadas"
                    metric={summary.refunded.records}
                    submetric={`R$ ${Parser.price.brl(summary.refunded.amount)}`}
                    loading={loading}
                />
                <MetricCard
                    title="Aguardando pagamento"
                    metric={summary.pending.records}
                    submetric={`R$ ${Parser.price.brl(summary.pending.amount)}`}
                    loading={loading}
                />
            </div>

            <ChargesTableSection />
        </div>
    )
}
